import { CloudActions, CostCenter, FlowjoCommons } from '@portal/portal-core';
import { put, takeLatest, call } from 'redux-saga/effects';

import { getOrCreateCostCenter, LicenseError, ccTypes } from '../shared';
import { sendAction, type SuperClientConfig, type WrapResult } from '../api';
import { showLicenseCreationSuccess, showLicenseCreationError } from '../actions';
import { SUPER_ADMIN_CREATE_LICENSE, SUPER_ADMIN_LICENSE_ERR } from '../types';

const { Alerts } = FlowjoCommons;

// A feature toggle carried in the create-license action's `enabledFeatures` map.
interface FeatureConfig {
  name: string;
  value: unknown;
  enabled: boolean;
}

// The create-license action dispatched into these sagas. Only the domain fields
// actually read below are modeled; the rest are passed straight through to cloud-core.
interface CreateLicenseAction {
  type: string;
  productName: string;
  productAccessType: string;
  ownerID: string;
  startDate?: Date;
  endDate?: Date;
  seatCount?: number;
  maxSharedComputers?: number;
  costCenterType: string;
  enabledFeatures?: Record<string, FeatureConfig>;
}

// Cost-center id threaded between the license sagas.
interface CostCenterIds {
  costCenterID: string;
}

// Cost-center + product-access ids threaded between the license sagas.
interface ProductIds {
  costCenterID: string;
  productAccessID: string;
}

// The single product-access entry we search for in the create-product response.
interface ProductAccessEntry {
  id: string;
  name: string;
}

// `sendAction` result narrowed to the create-product response shape this saga reads.
interface CreateProductResult extends WrapResult {
  data: { costCenter: { productAccess: ProductAccessEntry[] } };
}

// cloud-core's licenses-verbose creators accept an optional `limit`, but this saga
// intentionally invokes them with no arguments; model them as zero-arg creators.
type LicensesActionCreator = () => { type: string };

const getLicensesActionDict = new Map<string, LicensesActionCreator>([
  [ccTypes.individual, CloudActions.getIndividualLicensesVerbose as unknown as LicensesActionCreator],
  [
    ccTypes.institutional,
    CloudActions.getInstitutionalLicensesVerbose as unknown as LicensesActionCreator,
  ],
]);

export function* createProduct(
  config: SuperClientConfig,
  action: CreateLicenseAction,
  { costCenterID }: CostCenterIds,
): Generator<unknown, { productAccessID: string | undefined }, unknown> {
  // productName expected by the API in the body of the action
  const createProductAction = CloudActions.adminAddProductToCostCenter(
    action.productName,
    costCenterID,
    action.productAccessType,
    action.ownerID,
    action.startDate,
    action.endDate,
    action.seatCount,
    action.maxSharedComputers,
  );

  const res = (yield call(sendAction, config, createProductAction)) as CreateProductResult;
  // find the newly created product access
  const productAccess = res.data.costCenter.productAccess.find(
    (product: ProductAccessEntry) => product.name === action.productName,
  );
  // if undefined, return false
  const productAccessID = productAccess && productAccess.id;

  if (res.failed || !productAccessID) {
    throw new LicenseError([createProductAction.type]);
  }
  return {
    productAccessID,
  };
}

export function* addUser(
  config: SuperClientConfig,
  action: CreateLicenseAction,
  { costCenterID }: CostCenterIds,
): Generator<unknown, void, unknown> {
  const endDate = null;
  const addUserAction = CloudActions.adminAddUserToProduct(
    action.productName,
    costCenterID,
    action.productAccessType,
    action.ownerID,
    endDate,
  );

  const res = (yield call(sendAction, config, addUserAction)) as WrapResult;
  if (res.failed) {
    throw new LicenseError([addUserAction.type]);
  }
}

export function* updateSeatCount(
  config: SuperClientConfig,
  action: CreateLicenseAction,
  { costCenterID, productAccessID }: ProductIds,
): Generator<unknown, void, unknown> {
  const updateSeatCountAction = CloudActions.adminSetProductSeats(
    costCenterID,
    productAccessID,
    action.seatCount,
  );

  const res = (yield call(sendAction, config, updateSeatCountAction)) as WrapResult;
  if (res.failed) {
    throw new LicenseError([updateSeatCountAction.type]);
  }
}

export function* finalizeProduct(
  config: SuperClientConfig,
  action: CreateLicenseAction,
  { costCenterID, productAccessID }: ProductIds,
): Generator<unknown, void, unknown> {
  const { institutional, individual } = CostCenter.COST_CENTER_TYPES;

  if (action.costCenterType === institutional) {
    yield call(updateSeatCount, config, action, { costCenterID, productAccessID });
  } else if (action.costCenterType === individual) {
    yield call(addUser, config, action, { costCenterID });
  }
}

export function* createLicense(
  config: SuperClientConfig,
  action: CreateLicenseAction,
): Generator<unknown, void, unknown> {
  const { costCenterID } = (yield call(getOrCreateCostCenter, config, action)) as CostCenterIds;
  const { productAccessID } = (yield call(createProduct, config, action, {
    costCenterID,
  })) as ProductIds;
  yield call(finalizeProduct, config, action, {
    costCenterID,
    productAccessID,
  });

  // Add enabled features if any were selected (batched into single request)
  if (action.enabledFeatures && Object.keys(action.enabledFeatures).length > 0) {
    const enabledFeaturesArray = Object.values(action.enabledFeatures).filter(
      (feature: FeatureConfig) => feature.enabled,
    );

    if (enabledFeaturesArray.length > 0) {
      // Create all feature actions
      const featureActions = enabledFeaturesArray.map((feature: FeatureConfig) =>
        CloudActions.adminAddFeatureToProductAccess(
          feature.name,
          costCenterID,
          productAccessID,
          feature.value,
          feature.enabled,
        ),
      );

      // Batch all feature additions into a single request
      const batchAction = CloudActions.adminBatchActions(featureActions);
      const res = (yield call(sendAction, config, batchAction)) as WrapResult;

      if (res.failed) {
        // eslint-disable-next-line no-console
        console.error('Failed to add features to license:', res.error);
        const warningAlert = Alerts.Alert.AlertStamp({
          id: 'FEATURE_CREATION_FAILED',
          date: null,
          type: 'FEATURE_CREATION_FAILED',
        });
        yield put(Alerts.AlertActions.setAlert(warningAlert));
      }
    }
  }
}

// ------------------------------------------------------------------------------------------------

// Joint creation handler for FlowJo / SeqGeq
export function* handleCreateLicense(
  config: SuperClientConfig,
  action: CreateLicenseAction,
): Generator<unknown, void, unknown> {
  const getLicensesAction = getLicensesActionDict.get(action.costCenterType);
  try {
    yield call(createLicense, config, action);
    yield put(showLicenseCreationSuccess());
    if (getLicensesAction) yield put(getLicensesAction());
  } catch (err) {
    // `err` is `unknown` in a catch clause; narrow to the LicenseError-ish shape we read.
    const licenseErr = err as { type?: string; actions?: unknown[] };
    // Show modal for errors we know how to deal with:
    if (licenseErr.type === SUPER_ADMIN_LICENSE_ERR) {
      yield put(showLicenseCreationError(licenseErr.actions));
    } else {
      console.error(err);
    }
    if (getLicensesAction) yield put(getLicensesAction());
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchCreateLicense(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_CREATE_LICENSE, handleCreateLicense, config);
}
